import { useState, useEffect, useRef } from 'react';
import { Search, X, Filter } from 'lucide-react';
import { haptic } from '../lib/telegram';
import { t } from '../lib/i18n';
import { api } from '../lib/api';

interface SearchBarProps {
  onSearch: (query: string) => void;
  onFilterClick?: () => void;
  placeholder?: string;
  alwaysExpanded?: boolean;
}

export function SearchBar({ onSearch, onFilterClick, placeholder, alwaysExpanded = false }: SearchBarProps) {
  const [query, setQuery] = useState('');
  const [isExpanded, setIsExpanded] = useState(alwaysExpanded);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const translations = t();

  useEffect(() => {
    if (alwaysExpanded) {
      setIsExpanded(true);
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }, [alwaysExpanded]);

  useEffect(() => {
    if (isExpanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isExpanded]);

  // Fetch autocomplete suggestions as user types
  useEffect(() => {
    if (!query.trim() || query.length < 1) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const fetchSuggestions = async () => {
      try {
        const response = await api.search.suggestions(query);
        setSuggestions(response.suggestions || []);
        setShowSuggestions((response.suggestions || []).length > 0);
        setSelectedSuggestionIndex(-1);
      } catch (error) {
        // Silently fail - suggestions are optional
        setSuggestions([]);
        setShowSuggestions(false);
      }
    };

    const timeoutId = setTimeout(fetchSuggestions, 200);
    return () => clearTimeout(timeoutId);
  }, [query]);

  const handleIconClick = () => {
    setIsExpanded(true);
    haptic.light();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    haptic.light();
    setShowSuggestions(false);
    onSearch(query.trim());
  };

  const handleClear = () => {
    setQuery('');
    onSearch('');
    setSuggestions([]);
    setShowSuggestions(false);
    haptic.light();
  };

  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion);
    setShowSuggestions(false);
    onSearch(suggestion);
    haptic.selection();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) {
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSuggestionIndex((prev) => 
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSuggestionIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter' && selectedSuggestionIndex >= 0) {
      e.preventDefault();
      handleSuggestionClick(suggestions[selectedSuggestionIndex]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const handleBlur = () => {
    // Delay hiding suggestions to allow clicks
    setTimeout(() => {
      setShowSuggestions(false);
    }, 300);
    
    // Only collapse if there's no query and not always expanded
    if (!query && !alwaysExpanded) {
      setIsExpanded(false);
    }
  };

  const handleFocus = () => {
    if (query.trim() && suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  // Always show expanded version if alwaysExpanded is true
  if (alwaysExpanded) {
    return (
      <div className="flex items-center gap-3 mb-4">
        <form onSubmit={handleSubmit} className="flex-1 relative">
          <div className="relative">
            <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50 z-10" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={handleFocus}
              onBlur={handleBlur}
              placeholder={placeholder || translations.common.search}
              className={`w-full h-12 rounded-2xl glass border border-white/10 text-white placeholder-white/50 focus:border-splyt-green/50 focus:outline-none focus:ring-2 focus:ring-splyt-green/20 transition-all ${query ? 'pl-12 pr-12' : 'pl-4 pr-12'}`}
            />
            {query && (
              <button
                type="button"
                onClick={handleClear}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full flex items-center justify-center glass border border-white/10 hover:bg-white/10 transition-all z-10"
              >
                <X className="w-4 h-4 text-white/70" />
              </button>
            )}
            {/* Autocomplete suggestions dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <div
                ref={suggestionsRef}
                className="absolute top-full left-0 right-0 mt-2 rounded-2xl glass border border-white/10 overflow-hidden z-50 max-h-64 overflow-y-auto"
              >
                {suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleSuggestionClick(suggestion)}
                    className={`w-full text-left px-4 py-3 hover:bg-white/10 transition-all ${
                      index === selectedSuggestionIndex ? 'bg-white/10' : ''
                    } ${index === 0 ? '' : 'border-t border-white/5'}`}
                  >
                    <div className="flex items-center gap-2">
                      <Search className="w-4 h-4 text-white/50" />
                      <span className="text-white text-sm">{suggestion}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </form>
        {onFilterClick && (
          <button
            onClick={() => {
              onFilterClick();
              haptic.selection();
            }}
            className="w-12 h-12 rounded-2xl flex items-center justify-center glass border border-white/10 hover:border-splyt-green/50 hover:bg-white/10 transition-all"
          >
            <Filter className="w-5 h-5 text-white" />
          </button>
        )}
      </div>
    );
  }

  // Original dropdown logic for non-search page
  return (
    <div className="flex items-center justify-end gap-3 mb-4">
      {!isExpanded ? (
        <button
          onClick={handleIconClick}
          className="w-12 h-12 rounded-2xl flex items-center justify-center glass border border-white/10 hover:border-splyt-green/50 hover:bg-white/10 transition-all"
        >
          <Search className="w-5 h-5 text-white" />
        </button>
      ) : (
        <form onSubmit={handleSubmit} className="flex-1 relative animate-fade-in">
          <div className="relative">
            <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50 z-10" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={handleFocus}
              onBlur={handleBlur}
              placeholder={placeholder || translations.common.search}
              className={`w-full h-12 rounded-2xl glass border border-white/10 text-white placeholder-white/50 focus:border-splyt-green/50 focus:outline-none focus:ring-2 focus:ring-splyt-green/20 transition-all ${query ? 'pl-12 pr-12' : 'pl-4 pr-12'}`}
            />
            {query && (
              <button
                type="button"
                onClick={handleClear}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full flex items-center justify-center glass border border-white/10 hover:bg-white/10 transition-all z-10"
              >
                <X className="w-4 h-4 text-white/70" />
              </button>
            )}
            {/* Autocomplete suggestions dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <div
                ref={suggestionsRef}
                className="absolute top-full left-0 right-0 mt-2 rounded-2xl glass border border-white/10 overflow-hidden z-50 max-h-64 overflow-y-auto"
              >
                {suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleSuggestionClick(suggestion)}
                    className={`w-full text-left px-4 py-3 hover:bg-white/10 transition-all ${
                      index === selectedSuggestionIndex ? 'bg-white/10' : ''
                    } ${index === 0 ? '' : 'border-t border-white/5'}`}
                  >
                    <div className="flex items-center gap-2">
                      <Search className="w-4 h-4 text-white/50" />
                      <span className="text-white text-sm">{suggestion}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </form>
      )}
      {onFilterClick && (
        <button
          onClick={() => {
            onFilterClick();
            haptic.selection();
          }}
          className="w-12 h-12 rounded-2xl flex items-center justify-center glass border border-white/10 hover:border-splyt-green/50 hover:bg-white/10 transition-all"
        >
          <Filter className="w-5 h-5 text-white" />
        </button>
      )}
    </div>
  );
}

